// src/components/TeamProfitComponent.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { API_ENDPOINT } from "../../../../../lib/api-url";
import axios from "axios";
import useIndividualStream from "../../../../shared/hooks/useIndividualSocket";
import TimerMini from "../../../../shared/ui/timer-mini/TimerMini";

// ===== CONFIG =====
const TARGET_PROFIT_USD = 500; // positive goal in dollars (equity - balance)
const MAX_LOSS = -10; // red floor for the progress bar scale only (percent)

// Track last timestamps per team to ignore out-of-order frames
const lastTsByTeam = new Map();

// ===== ASSETS =====
const assets = {
  bullmasters: {
    banner: "/profit-wall/bullmasters-profit-banner.svg",
    box: "/profit-wall/bullmasters-profit-box.svg",
    logo: "/teams/bullmaster-logo.webp",
  },
  ironbears: {
    banner: "/profit-wall/ironbears-profit-banner.svg",
    box: "/profit-wall/ironbears-profit-box.svg",
    logo: "/teams/ironbears-logo.webp",
  },
  alphawolves: {
    banner: "/profit-wall/alphawolves-profit-banner.svg",
    box: "/profit-wall/alphawolves-profit-box.svg",
    logo: "/teams/alphawolves-logo.webp",
  },
  goldeneagles: {
    banner: "/profit-wall/goldeneagles-profit-banner.svg",
    box: "/profit-wall/goldeneagles-profit-box.svg",
    logo: "/teams/goldeneagles-logo.webp",
  },
};

const expiredLogoMap = {
  bullmasters: "/profit-wall/expired-bullmasters-logo.svg",
  ironbears: "/profit-wall/expired-ironbears-logo.svg",
  alphawolves: "/profit-wall/expired-alphawolves-logo.svg",
  goldeneagles: "/profit-wall/expired-goldeneagles-logo.svg",
};

const expiredLogoStyleMap = {
  bullmasters: {
    width: "86px",
    top: "50%",
    left: "47.5%",
    transform: "translate(50%, -50%)",
  },
  ironbears: {
    width: "96px",
    top: "50%",
    left: "35.5%",
    transform: "translate(50%, -50%)",
  },
  alphawolves: {
    width: "110px",
    top: "50%",
    left: "47.5%",
    transform: "translate(50%, -50%)",
  },
  goldeneagles: {
    width: "110px",
    top: "50%",
    left: "47.5%",
    transform: "translate(50%, -50%)",
  },
};

// ===== HELPERS =====
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}
function toSlugFromName(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/\s+/g, "");
}
function formatSignedPct(pct) {
  if (!isFinite(pct)) return "0.00%";
  const sign = pct > 0 ? "+" : pct < 0 ? "" : "";
  return `${sign}${pct.toFixed(2)}%`;
}
function safeNum(v) {
  if (v == null) return null;
  const n = Number.parseFloat(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

// Strict API-name → asset-key mapping
function keyFromApiName(apiName) {
  const n = String(apiName || "")
    .trim()
    .toUpperCase();
  const map = {
    BULLMASTERS: "bullmasters",
    IRONBEARS: "ironbears",
    ALPHAWOLVES: "alphawolves",
    GOLDENEAGLES: "goldeneagles",
  };
  return map[n] || "bullmasters";
}

// ===== STREAM PARSING =====
// Tolerant parser: supports both flat frames and { RiskEvent: {...} }
function parseStreamValues(evt) {
  try {
    const re = evt?.RiskEvent ?? evt ?? {};

    // login can be string/number
    const loginRaw = re?.login ?? re?.risk_state?.login;
    const login = loginRaw != null ? String(loginRaw) : null;

    // BALANCE: prefer current_balance, then corrected_value, then old/new
    const bal =
      safeNum(re?.current_balance) ??
      safeNum(re?.corrected_value) ??
      safeNum(re?.old_value) ??
      safeNum(re?.current_balance_amount);

    // EQUITY: prefer current_equity, then equity, then new_value
    const eq =
      safeNum(re?.current_equity) ??
      safeNum(re?.equity) ??
      safeNum(re?.new_value);

    // SOD balance for percentage display
    const sod =
      safeNum(re?.start_of_the_day_balance) ??
      safeNum(re?.risk_state?.opening_snapshot?.daily_starting_balance);

    // Patch missing values from message if possible
    if ((eq == null || bal == null) && typeof re?.message === "string") {
      const mEq = re.message.match(/Equity=\$([0-9.\-]+)/i);
      const mBal = re.message.match(/Balance=\$([0-9.\-]+)/i);
      if (eq == null && mEq) {
        const v = safeNum(mEq[1]);
        if (v != null) re.__msg_equity = v;
      }
      if (bal == null && mBal) {
        const v = safeNum(mBal[1]);
        if (v != null) re.__msg_balance = v;
      }
    }

    const equity = eq ?? re.__msg_equity ?? null;
    const currentBalance = bal ?? re.__msg_balance ?? null;

    const isFailedRaw =
      re?.is_account_failed ??
      re?.risk_state?.is_account_failed ??
      re?.risk_state?.has_breached_overall_limits;
    const isFailed =
      typeof isFailedRaw === "string"
        ? isFailedRaw.toLowerCase() === "true"
        : Boolean(isFailedRaw);

    // timestamp can be nested or flat
    const tsStr =
      re?.timestamp ?? re?.risk_state?.last_update ?? evt?.timestamp;
    const ts = Date.parse(tsStr || Date.now());

    // DEBUG: log parsed values
    // eslint-disable-next-line no-console
    console.log("[profit-wall] parsed frame", {
      login,
      equity,
      currentBalance,
      startOfDayBalance: sod,
      isFailed,
      ts: new Date(ts).toISOString(),
      raw: evt,
    });

    return {
      login,
      currentBalance,
      equity,
      startOfDayBalance: sod ?? null,
      isFailed,
      ts,
    };
  } catch (e) {
    console.error("[profit-wall] parseStreamValues error:", e, { evt });
    return {
      login: null,
      currentBalance: null,
      equity: null,
      startOfDayBalance: null,
      isFailed: false,
      ts: Date.now(),
    };
  }
}

// % from start-of-day (used for display and loss side)
function calcPctFromDayStart(currentBalance, startOfDayBalance) {
  if (
    !isFinite(currentBalance) ||
    !isFinite(startOfDayBalance) ||
    startOfDayBalance === 0
  )
    return 0;
  const pct = ((currentBalance - startOfDayBalance) / startOfDayBalance) * 100;
  return Math.round(pct * 100) / 100;
}

// ===== One-winner persistence (reuse keys; now means $500 reach) =====
const WINNER_KEY = "fivePercentWinnerTeamId";
const WINNER_CALLED_KEY = "fivePercentWinnerCalled";
function loadWinner() {
  if (typeof localStorage === "undefined") return { id: null, called: false };
  try {
    const id = localStorage.getItem(WINNER_KEY);
    const called = localStorage.getItem(WINNER_CALLED_KEY) === "true";
    return { id: id || null, called };
  } catch {
    return { id: null, called: false };
  }
}
function persistWinner(id) {
  try {
    localStorage?.setItem(WINNER_KEY, id);
  } catch {}
}
function persistWinnerCalled(called) {
  try {
    localStorage?.setItem(WINNER_CALLED_KEY, String(!!called));
  } catch {}
}

// ===== UI PARTS =====
const ProgressBar = React.memo(function ProgressBar({ profitPct, profitUsd }) {
  if (profitPct < 0) {
    const lossPct = clamp(
      (Math.abs(profitPct) / Math.abs(MAX_LOSS)) * 100,
      0,
      100
    );
    return (
      <div
        className="w-[300px] h-[8px] rounded-[15px] absolute "
        style={{ right: "20%", top: "46%", transform: "translate(-50%, -50%)" }}
      >
        <motion.div
          className="absolute right-0 top-0 h-full rounded-[15px]"
          animate={{ width: `${lossPct}%`, opacity: 1 }}
          transition={{ duration: 0.35 }}
          style={{
            background: "linear-gradient(90deg, #910002 0%, #C42225 100%)",
          }}
        />
      </div>
    );
  }
  const dollars = Math.max(0, profitUsd || 0);
  const fillPct = clamp((dollars / TARGET_PROFIT_USD) * 100, 0, 100);
  return (
    <div
      className="w-[200px] h-[8px] rounded-[15px] absolute overflow-hidden"
      style={{ left: "45%", top: "47%", transform: "translate(50%, -50%)" }}
    >
      <motion.div
        className="h-full rounded-[15px]"
        animate={{ width: `${fillPct}%`, opacity: 1 }}
        transition={{ duration: 0.35 }}
        style={{
          background:
            "linear-gradient(90deg, rgba(64, 191, 106, 0) 0%, #40BF6A 50%, #40BF6A 100%)",
        }}
      />
    </div>
  );
});

const TeamCard = React.memo(
  function TeamCard({ team }) {
    const isExpired = Boolean(team.isFailed);
    const basePack = assets[team.key] || assets.bullmasters;
    const bannerSrc = isExpired
      ? "/profit-wall/expired-profit.svg"
      : basePack.banner;
    const boxSrc = basePack.box;
    const logoSrc = isExpired
      ? expiredLogoMap[team.key] || basePack.logo
      : basePack.logo;

    const logoSizeMap = {
      bullmasters: "w-[110px] z-10",
      ironbears: "w-[150px] z-10",
      alphawolves: "w-[180px] z-20",
      goldeneagles: "w-[180px] z-20",
    };
    const liveLogoClass = logoSizeMap[team.key] || "w-[110px] z-10";
    const expiredLogoStyle = expiredLogoStyleMap[team.key] || {};
    const logoClass = isExpired ? "z-30" : `${liveLogoClass}`;

    // ALWAYS show percentage in the center
    const liveStr = formatSignedPct(team.profitPct);
    const liveColor = team.profitPct >= 0 ? "#40BF6A" : "#C40000";

    return (
      <motion.div
        layout
        transition={{ type: "spring", stiffness: 120, damping: 18 }}
        className="team-container flex flex-col items-center justify-center relative w-[600px] h-[200px]"
      >
        <div className="w-fit relative">
          <div className="value-container w-[600px] flex items-center justify-between right-[50%] top-[-24px] translate-x-[50%] translate-y-[-50%] absolute z-20">
            <span
              style={{ fontFamily: "Etemad" }}
              className="text-[#C40000] text-[20px] font-normal"
            >
              -10%
            </span>
            <span
              style={{ fontFamily: "Etemad", color: liveColor }}
              className="text-[20px] font-semibold mb-[62px] ml-[54px]"
            >
              {liveStr}
            </span>
            <span
              style={{ fontFamily: "Etemad" }}
              className="text-[#40BF6A] text-[20px] font-normal"
            >
              +500$
            </span>
          </div>

          <img
            className={`w-[625px] ${isExpired ? "mb-[7px]" : ""}`}
            src={bannerSrc}
            alt="banner"
          />
          <img
            className=" right-[48%] top-[50%] translate-x-[50%] translate-y-[-50%] absolute z-10"
            src={boxSrc}
            alt="box"
          />
          <img
            className={`${logoClass} absolute`}
            src={logoSrc}
            alt="logo"
            style={
              isExpired
                ? {
                    right: "47.5%",
                    top: "50%",
                    transform: "translate(50%, -50%)",
                    ...expiredLogoStyle,
                  }
                : {
                    right: "47.5%",
                    top: "50%",
                    transform: "translate(50%, -50%)",
                  }
            }
          />
          <ProgressBar profitPct={team.profitPct} profitUsd={team.profitUsd} />
        </div>
      </motion.div>
    );
  },
  (prev, next) =>
    prev.team.id === next.team.id &&
    prev.team.profitPct === next.team.profitPct &&
    prev.team.profitUsd === next.team.profitUsd &&
    prev.team.isFailed === next.team.isFailed &&
    prev.team.key === next.team.key
);

// ===== MAIN COMPONENT =====
export default function TeamProfitComponent() {
  const [teams, setTeams] = useState([]);
  const [loginIndex, setLoginIndex] = useState({});

  const rafRef = useRef(null);
  const pendingRef = useRef(null);
  const winnerRef = useRef(loadWinner());

  const { lastItem } = useIndividualStream({
    url: "wss://eventproxy.ahangariholding.com/team?token=client_secret_token",
    token:
      (typeof localStorage !== "undefined" && localStorage.getItem("token")) ||
      "",
    enabled: true,
  });

  // Trigger when USD PnL (equity - balance) >= $500
  const maybeChooseAndTriggerWinner = async (teamId, profitUsd) => {
    if (!isFinite(profitUsd) || profitUsd < TARGET_PROFIT_USD) return;
    if (!winnerRef.current.id) {
      winnerRef.current = { id: teamId, called: false };
      persistWinner(teamId);
    }
    if (winnerRef.current.id !== teamId) return;
    if (winnerRef.current.called) return;

    try {
      await axios.post(API_ENDPOINT.initalepercentage.getInitialPercentage, {
        team_id: teamId,
      });
      winnerRef.current.called = true;
      persistWinnerCalled(true);
      console.log(
        "[profit-wall] WINNER API called for team",
        teamId,
        "profitUsd=",
        profitUsd
      );
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409 || status === 208) {
        winnerRef.current.called = true;
        persistWinnerCalled(true);
        console.log(
          "[profit-wall] WINNER already called (status",
          status,
          ") for team",
          teamId
        );
      } else {
        console.error("winner ($500) call failed:", err);
      }
    }
  };

  // map teams — STRICT: only use the "main" account login
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(API_ENDPOINT.wallTeams.sabziTeams);
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];

        const nextTeams = [];
        const nextLoginIndex = {};

        list.forEach((t) => {
          const key = keyFromApiName(t.name);
          const slug = toSlugFromName(t.name);

          // STRICT main account only
          const mainAcc = (t.team_accounts || []).find(
            (a) => String(a.type).toLowerCase() === "main"
          );

          if (!mainAcc) {
            console.warn(
              "[profit-wall] team has no MAIN account; skipping team",
              { teamId: t.id, name: t.name, team_accounts: t.team_accounts }
            );
            return; // skip this team entirely
          }

          const mainLogin = String(mainAcc.login);

          nextTeams.push({
            id: String(t.id),
            name: t.name,
            key,
            slug,
            profitPct: 0, // % from start-of-day (display + loss)
            profitUsd: 0, // USD equity - balance (progress + API trigger)
            currentBalance: null,
            equity: null,
            startOfDayBalance: null,
            isFailed: false,
            logins: [mainLogin], // ONLY the main login is tracked
          });

          nextLoginIndex[mainLogin] = nextTeams.length - 1; // index main only
        });

        if (mounted) {
          setTeams(nextTeams);
          setLoginIndex(nextLoginIndex);
          console.log("[profit-wall] teams (main-only) initialized", nextTeams);
          console.log("[profit-wall] loginIndex (main-only)", nextLoginIndex);
        }
      } catch (err) {
        console.error("Failed to load teams:", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // stream updates — coalesced via rAF to avoid flicker
  useEffect(() => {
    if (!lastItem) return; // accept flat frames too

    const { login, currentBalance, equity, startOfDayBalance, isFailed, ts } =
      parseStreamValues(lastItem);

    if (!login) {
      console.warn("[profit-wall] incoming frame without login, ignored");
      return;
    }

    const idx = loginIndex[login];
    if (idx === undefined) {
      console.warn("[profit-wall] socket login not in MAIN logins; ignoring", {
        login,
        knownMainLogins: Object.keys(loginIndex),
      });
      return;
    }

    pendingRef.current = {
      idx,
      currentBalance,
      equity,
      startOfDayBalance,
      ts,
      login,
      isFailed,
    };

    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        const pending = pendingRef.current;
        rafRef.current = null;
        pendingRef.current = null;
        if (!pending) return;

        setTeams((prev) => {
          const draft = [...prev];
          const t = { ...draft[pending.idx] };

          // Ignore out-of-order frames
          const lastTs = lastTsByTeam.get(t.id) || 0;
          if (pending.ts <= lastTs) {
            console.log("[profit-wall] stale frame ignored", {
              teamId: t.id,
              lastTs,
              ts: pending.ts,
            });
            return prev;
          }

          // 1) % from day start (display + loss progress)
          const sod =
            isFinite(pending.startOfDayBalance) &&
            pending.startOfDayBalance !== 0
              ? pending.startOfDayBalance
              : isFinite(t.startOfDayBalance) && t.startOfDayBalance !== 0
              ? t.startOfDayBalance
              : null;

          const nextPct =
            sod !== null && isFinite(pending.currentBalance)
              ? calcPctFromDayStart(pending.currentBalance, sod)
              : t.profitPct ?? 0;

          // 2) USD PnL = equity - balance (progress + API trigger)
          const bal = isFinite(pending.currentBalance)
            ? pending.currentBalance
            : t.currentBalance;
          const eq = isFinite(pending.equity) ? pending.equity : t.equity;
          const nextUsd =
            isFinite(eq) && isFinite(bal) ? eq - bal : t.profitUsd ?? 0;

          const nextFailed = Boolean(pending.isFailed);

          const changed =
            nextPct !== t.profitPct ||
            nextUsd !== t.profitUsd ||
            nextFailed !== Boolean(t.isFailed) ||
            bal !== t.currentBalance ||
            eq !== t.equity ||
            sod !== t.startOfDayBalance;

          if (!changed) {
            lastTsByTeam.set(t.id, pending.ts);
            console.log("[profit-wall] no changes for team", t.id, {
              nextPct,
              nextUsd,
              nextFailed,
            });
            return prev;
          }

          t.currentBalance = bal ?? t.currentBalance;
          t.equity = eq ?? t.equity;
          if (isFinite(sod)) t.startOfDayBalance = sod;
          t.profitPct = isFinite(nextPct) ? nextPct : t.profitPct;
          t.profitUsd = isFinite(nextUsd) ? nextUsd : t.profitUsd;
          t.isFailed = nextFailed;

          draft[pending.idx] = t;
          lastTsByTeam.set(t.id, pending.ts);

          console.log("[profit-wall] UPDATED team", t.id, {
            login: pending.login,
            profitPct: t.profitPct,
            profitUsd: t.profitUsd,
            sod: t.startOfDayBalance,
            equity: t.equity,
            balance: t.currentBalance,
          });

          // Trigger API at $500
          if (isFinite(t.profitUsd))
            maybeChooseAndTriggerWinner(t.id, t.profitUsd);

          return draft;
        });
      });
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingRef.current = null;
    };
  }, [lastItem, loginIndex]);

  // Sort by progress: prefer USD when positive; else by percentage (less negative is higher)
  const ranked = useMemo(() => {
    const copy = teams.map((t) => ({ ...t, expired: Boolean(t.isFailed) }));
    copy.sort((a, b) => {
      if (a.profitUsd >= 0 && b.profitUsd >= 0)
        return b.profitUsd - a.profitUsd;
      if (a.profitUsd >= 0 && b.profitUsd < 0) return -1;
      if (a.profitUsd < 0 && b.profitUsd >= 0) return 1;
      const ap = isFinite(a.profitPct) ? a.profitPct : -Infinity;
      const bp = isFinite(b.profitPct) ? b.profitPct : -Infinity;
      return bp - ap;
    });
    return copy;
  }, [teams]);

  useEffect(() => {
    console.log(
      "[profit-wall] ranked teams snapshot",
      ranked.map((t) => ({
        id: t.id,
        name: t.name,
        logins: t.logins,
        pct: t.profitPct,
        usd: t.profitUsd,
      }))
    );
  }, [ranked]);

  return (
    <div className="w-full h-full flex items-center justify-center flex-col relative">
      <div className=" absolute left-[30%] bottom-[38%] z-20 -translate-x-1/2 -translate-y-1/2">
        <TimerMini />
      </div>
      <LayoutGroup>
        <div className="flex flex-col gap-2 py-6">
          <AnimatePresence initial={false}>
            {ranked.map((t) => (
              <TeamCard key={t.id} team={t} />
            ))}
          </AnimatePresence>
        </div>
      </LayoutGroup>
    </div>
  );
}
